import { gaEvent, hasAnalyticsConsent } from "@/lib/googleTag";
const SESSION_KEY = "sporgmig_analytics_session_v1";
const ATTRIBUTION_KEY = "sporgmig_analytics_attribution_v1";
const LANDING_KEY = "sporgmig_analytics_landing_v1";
const REFERRER_KEY = "sporgmig_analytics_referrer_v1";
const SENT_KEY = "sporgmig_analytics_sent_v1";
const CONSENT_KEY = "analytics_consent";

const randomId = () => globalThis.crypto?.randomUUID?.() || `${Date.now()}_${Math.random().toString(36).slice(2)}`;

export function getAnalyticsSessionId() {
  if (localStorage.getItem(CONSENT_KEY) !== "granted") return "";
  let id = sessionStorage.getItem(SESSION_KEY);
  if (!id) {
    id = randomId();
    sessionStorage.setItem(SESSION_KEY, id);
  }
  return id;
}

let entryContext;
const hasCampaign = value => Object.values(value || {}).some(Boolean);

export function captureEntryContext() {
  const params = new URLSearchParams(window.location.search);
  const attribution = Object.fromEntries(['source', 'medium', 'campaign', 'term', 'content'].map(key => [key, params.get(`utm_${key}`) || null]));
  attribution.gclid = params.get('gclid') || null;
  if (!entryContext || (!hasCampaign(entryContext.attribution) && hasCampaign(attribution))) {
    entryContext = { attribution, landing: `${window.location.pathname}${window.location.search}`, referrer: document.referrer || '' };
  }
  return entryContext;
}

export function captureAttribution() {
  const entry = captureEntryContext();
  if (!hasAnalyticsConsent()) return {};
  let attribution = {};
  try { attribution = JSON.parse(sessionStorage.getItem(ATTRIBUTION_KEY) || '{}'); } catch {}
  if (!hasCampaign(attribution)) {
    attribution = entry.attribution;
    sessionStorage.setItem(ATTRIBUTION_KEY, JSON.stringify(attribution));
    sessionStorage.setItem(LANDING_KEY, entry.landing);
    sessionStorage.setItem(REFERRER_KEY, entry.referrer);
  }
  return attribution;
}

export function deviceType() {
  const width = window.innerWidth;
  return width < 768 ? "mobile" : width < 1024 ? "tablet" : "desktop";
}

export function getAnalyticsHeaders() {
  if (localStorage.getItem(CONSENT_KEY) !== "granted") return {};
  return {
    "X-Analytics-Session": getAnalyticsSessionId(),
    "X-Analytics-Attribution": JSON.stringify(captureAttribution()),
    "X-Analytics-Landing-Page": sessionStorage.getItem(LANDING_KEY) || "",
    "X-Analytics-Referrer": sessionStorage.getItem(REFERRER_KEY) || "",
    "X-Analytics-Device": deviceType(),
  };
}

function alreadySent(instanceId) {
  let sent = [];
  try { sent = JSON.parse(sessionStorage.getItem(SENT_KEY) || "[]"); } catch {}
  if (sent.includes(instanceId)) return true;
  sessionStorage.setItem(SENT_KEY, JSON.stringify([...sent.slice(-199), instanceId]));
  return false;
}

export function trackEvent(eventName, payload = {}, instanceId = randomId()) {
  if (localStorage.getItem(CONSENT_KEY) !== "granted") return;
  captureAttribution();
  if (alreadySent(instanceId)) return;
  const body = {
    event_name: eventName,
    event_instance_id: instanceId,
    expert_id: payload.expert_id || null,
    category: payload.category || null,
    landing_page: sessionStorage.getItem(LANDING_KEY) || null,
    referrer: sessionStorage.getItem(REFERRER_KEY) || null,
    device_type: deviceType(),
    metadata: payload.metadata || {},
  };
  const token = localStorage.getItem("en_token");
  fetch(`${process.env.REACT_APP_BACKEND_URL}/api/analytics/track`, {
    method: "POST",
    credentials: "include",
    keepalive: true,
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...getAnalyticsHeaders(),
    },
    body: JSON.stringify(body),
  }).catch(() => {});
  gaEvent(eventName, { expert_id: body.expert_id, category: body.category });
}

export function clearAnalyticsStorage() {
  [SESSION_KEY, ATTRIBUTION_KEY, LANDING_KEY, REFERRER_KEY, SENT_KEY].forEach((key) => sessionStorage.removeItem(key));
}

export function resetAnalyticsForTests() {
  clearAnalyticsStorage();
  entryContext = null;
}
